import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, ArrowUpRight, Sparkles } from "lucide-react";
import { scrollToId } from "@/lib/smoothScroll";
import { IMAGES } from "@/data/content";

const Line = ({ children, delay = 0, className = "" }) => (
  <span className={`block overflow-hidden pb-[0.08em] -mb-[0.08em] ${className}`}>
    <motion.span
      className="block"
      initial={{ y: "112%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

const STATS = [
  { value: "02", label: "Flagship case studies" },
  { value: "07", label: "Content channels" },
  { value: "30+", label: "Deliverables shipped" },
  { value: "00", label: "Unverified claims" },
];

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const textY = useTransform(scrollYProgress, [0, 1], [0, -60]);

  return (
    <section ref={ref} className="relative min-h-screen flex flex-col justify-center overflow-hidden pt-28 pb-16" data-testid="hero-section">
      <div className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-20 w-full grid lg:grid-cols-12 gap-12 lg:gap-8 items-center">
        <motion.div style={{ y: textY }} className="lg:col-span-7 relative z-10">
          <motion.div
            initial={{ opacity: 0, x: -16 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.7, delay: 0.4 }}
            className="flex items-center gap-3 mb-8"
          >
            <span className="relative flex h-2.5 w-2.5">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-wine opacity-60" />
              <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-wine" />
            </span>
            <span className="font-mono text-[11px] uppercase tracking-[0.22em] text-subtle" data-testid="availability-badge">
              Booking new projects — Q4 2026
            </span>
          </motion.div>

          <p className="font-mono text-xs uppercase tracking-[0.24em] text-amber mb-6">
            <Line delay={0.35}>Melinda Adams — Copywriter &amp; Content Writer</Line>
          </p>

          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.06] text-ink" data-testid="hero-heading">
            <Line delay={0.5}>Tech that works</Line>
            <Line delay={0.62}>for you. <em className="text-wine">Not the</em></Line>
            <Line delay={0.74}><em className="text-wine">other way</em> around.</Line>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.05 }}
            className="mt-8 max-w-xl text-base sm:text-lg leading-relaxed text-subtle"
          >
            I translate product value into clear, distinctive customer language — combining
            audience insight, editorial craft, conversion principles, and a practical
            understanding of how content moves through marketing, product, and operations teams.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.2 }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <button
              onClick={() => scrollToId("work")}
              data-testid="hero-cta-samples-button"
              className="group inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-ink text-paper font-mono text-xs uppercase tracking-[0.18em] hover:bg-wine transition-colors duration-300"
            >
              Read the Work
              <ArrowDown size={14} className="transition-transform duration-300 group-hover:translate-y-0.5" />
            </button>
            <button
              onClick={() => scrollToId("contact")}
              data-testid="hero-cta-contact-button"
              className="group inline-flex items-center gap-2 px-7 py-3.5 rounded-full border border-ink/25 font-mono text-xs uppercase tracking-[0.18em] hover:border-wine hover:text-wine transition-colors duration-300"
            >
              Start an Enquiry
              <ArrowUpRight size={14} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </button>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1.5 }}
            className="mt-14 grid grid-cols-2 sm:grid-cols-4 gap-6 border-t border-line pt-8"
            data-testid="hero-stats"
          >
            {STATS.map((s) => (
              <div key={s.label}>
                <div className="font-serif text-3xl text-wine">{s.value}</div>
                <div className="mt-1 font-mono text-[10px] uppercase tracking-[0.16em] text-subtle">{s.label}</div>
              </div>
            ))}
          </motion.div>
        </motion.div>

        <div className="lg:col-span-5 relative hidden lg:block">
          <motion.div
            initial={{ opacity: 0, scale: 0.94, rotate: 2 }}
            animate={{ opacity: 1, scale: 1, rotate: 0 }}
            transition={{ duration: 1.2, delay: 0.7, ease: [0.22, 1, 0.36, 1] }}
            className="relative"
          >
            <div className="spotlight-frame vignette relative overflow-hidden border border-ink/10" data-testid="hero-portrait-frame">
              <motion.img
                style={{ y: imgY, scale: 1.08 }}
                src={IMAGES.portrait}
                alt="Melinda Adams, copywriter and content writer"
                className="w-full h-[520px] object-cover object-[50%_18%]"
              />
            </div>
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 1.4 }}
              className="absolute -bottom-8 -left-10 bg-cream border border-line px-6 py-5 shadow-[0_20px_40px_-20px_rgba(26,24,21,0.35)] max-w-[240px]"
            >
              <div className="flex items-center gap-2 mb-2 text-amber">
                <Sparkles size={13} />
                <span className="font-mono text-[10px] uppercase tracking-[0.18em]">Currently drafting</span>
              </div>
              <p className="font-serif italic text-lg leading-snug">
                "Every story deserves a place to stay."
              </p>
            </motion.div>
          </motion.div>
        </div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2, duration: 1 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 hidden sm:block"
      >
        <motion.div animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 2, ease: "easeInOut" }}>
          <ArrowDown size={18} className="text-subtle" />
        </motion.div>
      </motion.div>
    </section>
  );
};
